/**
 * AircraftLoaderService
 * Loads and manages 3D aircraft models (GLTF) for rendering in the map scene
 *
 * Key design:
 * - Single shared instance (singleton) across all consumers
 * - Model cache keyed by typeCode — load from network once per type per session
 * - pendingLoads map deduplicates concurrent requests for the same type
 * - Single clone per instance (no double-clone)
 */

import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js'
import { FEET_TO_METERS } from '@/constants/units'
import type { AircraftCategory, GearType } from '@/types/aircraft-types'
import type { AllocationRenderMode } from '@/types/allocations'
import { ProceduralAircraftFactory } from './procedural'
import {
  getBodyMaterial,
  getAccentMaterial,
  getGhostAwayBodyMaterial,
  getGhostAwayAccentMaterial,
  getGhostIncomingBodyMaterial,
  getGhostIncomingAccentMaterial,
  getGhostReturningBodyMaterial,
  getGhostReturningAccentMaterial,
} from './procedural/materials'

/** LOD model entry with URL and optional metadata */
interface LodModel {
  url: string
  size_mb?: number
}

// Shared geometry/material for LOD-low simplified boxes. One allocation reused
// across every box so swapping 124+ aircraft to far-zoom boxes (and back)
// doesn't churn GPU buffers. Meshes carry `userData.shared = true` so disposal
// skips these shared resources.
let _sharedBoxGeometry: THREE.BoxGeometry | null = null
let _sharedBoxMaterial: THREE.MeshStandardMaterial | null = null
function getSharedBoxGeometry(): THREE.BoxGeometry {
  if (!_sharedBoxGeometry) {
    _sharedBoxGeometry = new THREE.BoxGeometry(
      15 * FEET_TO_METERS, // wingspan-ish
      3 * FEET_TO_METERS, // height
      30 * FEET_TO_METERS // length
    )
  }
  return _sharedBoxGeometry
}
function getSharedBoxMaterial(): THREE.MeshStandardMaterial {
  if (!_sharedBoxMaterial) {
    _sharedBoxMaterial = new THREE.MeshStandardMaterial({ color: 0xaabbcc, roughness: 0.8 })
  }
  return _sharedBoxMaterial
}

/**
 * Model settings stored in aircraft_types.model_settings JSON column.
 * All properties are optional - missing values use defaults.
 */
export interface ModelSettings {
  /** Path to GLB model file (resolved from model_url or models.lod1.url) */
  filePath?: string
  /** Uniform scale multiplier (default: 1) */
  scale?: number
  /** Base rotation [x, y, z] in degrees to orient model correctly (default: [0,0,0]) */
  rotation?: [number, number, number]
  /** Vertical offset in feet to prevent ground clipping (default: 0) */
  yOffset?: number
  /** Which axis to use for heading rotation (default: 'y') */
  headingAxis?: 'x' | 'y' | 'z'
  /** Which axis is "up" in the model (default: 'y') */
  verticalAxis?: 'x' | 'y' | 'z'
  /** Legacy nested LOD models structure */
  models?: {
    lod0?: LodModel
    lod1?: LodModel
    lod2?: LodModel
    lod3?: LodModel
  }
  /** Pre-rendered view images from R2 (used for 2D sprite previews) */
  images?: {
    top?: string
    side?: string
    front?: string
    perspective?: string
  }
}

/** Aircraft type with 3D model configuration */
export interface AircraftType3D {
  id: string
  typeCode: string
  modelSettings: ModelSettings
}

/** Physical dimensions for procedural aircraft rendering */
export interface AircraftPhysicalDimensions {
  length: number              // feet
  wingspan: number            // feet
  height: number              // feet
  tailHeight: number | null   // feet
  category: AircraftCategory | null
  gearType?: GearType | null
}

/** Placed aircraft with position data */
export interface PlacedAircraft3D {
  id: string
  aircraftId: string
  tailNumber: string
  typeId: string
  // Position in feet (relative to scene center)
  position: [number, number, number]
  // Rotation in degrees (heading)
  rotation: number
  // Scale multiplier
  scale: number
  // Aircraft type info for model loading
  aircraftType: AircraftType3D
  // Physical dimensions for procedural rendering (optional)
  physicalDimensions?: AircraftPhysicalDimensions
  // Render mode: solid, ghost_away, ghost_incoming, ghost_returning
  renderMode?: AllocationRenderMode
}

/**
 * Service for loading and managing aircraft 3D models
 */
export class AircraftLoaderService {
  private loader: GLTFLoader
  private modelCache: Map<string, THREE.Object3D>
  /** In-flight network loads — concurrent callers for the same typeCode share one promise */
  private pendingLoads: Map<string, Promise<THREE.Object3D>>
  private enableShadows: boolean

  constructor(enableShadows: boolean = true) {
    this.loader = new GLTFLoader()
    // Enable meshopt decoder for compressed GLTF models
    this.loader.setMeshoptDecoder(MeshoptDecoder)
    this.modelCache = new Map()
    this.pendingLoads = new Map()
    this.enableShadows = enableShadows
  }

  /**
   * Load an aircraft model from GLTF file or generate procedurally.
   * - forceProceduralMode → always use procedural generation
   * - No filePath + has dimensions → procedural generation
   * - No filePath + no dimensions → gray box placeholder fallback
   * - Cache hit → instant clone
   * - In-flight load for same type → joins existing promise (no duplicate network request)
   * - Cache miss → starts network load, caches template, returns clone
   */
  async loadAircraft(aircraft: PlacedAircraft3D, forceProceduralMode: boolean = false): Promise<THREE.Object3D> {
    const { aircraftType } = aircraft
    const typeCode = aircraftType.typeCode
    const filePath = aircraftType.modelSettings.filePath
    const renderMode = aircraft.renderMode ?? 'solid'

    let model: THREE.Object3D

    // Procedural path: forced mode or no GLTF model available
    if (forceProceduralMode || !filePath) {
      if (aircraft.physicalDimensions) {
        model = this.createProceduralModel(aircraft)
      } else {
        console.warn(`No model or dimensions for aircraft ${aircraft.tailNumber}`)
        model = this.createPlaceholderModel(aircraft)
      }
    } else {
      // 1. Cache hit — instant clone (single clone, no double-clone)
      const cached = this.modelCache.get(typeCode)
      if (cached) {
        model = this.createInstanceFromTemplate(cached, aircraft)
      } else {
        // 2. Join in-flight load or start new one (dedup concurrent requests)
        if (!this.pendingLoads.has(typeCode)) {
          this.pendingLoads.set(typeCode, this.loadFromNetwork(filePath, typeCode, aircraft.tailNumber))
        }

        try {
          const template = await this.pendingLoads.get(typeCode)!
          model = this.createInstanceFromTemplate(template, aircraft)
        } catch {
          model = this.createPlaceholderModel(aircraft)
        }
      }
    }

    // Store renderMode on all models for diff detection
    model.userData.renderMode = renderMode

    // Apply ghost materials for non-solid render modes
    if (renderMode !== 'solid') {
      this.applyGhostMaterials(model, renderMode)
    }

    return model
  }

  /**
   * Whether loading this aircraft would trigger a network model fetch (as
   * opposed to an instant procedural build or a clone from the in-memory
   * cache). Used by throttled insertion queues to pace cached vs network loads
   * differently (e.g. 100ms vs 500ms spacing).
   */
  needsNetworkLoad(aircraft: PlacedAircraft3D, forceProceduralMode = false): boolean {
    const filePath = aircraft.aircraftType.modelSettings.filePath
    if (forceProceduralMode || !filePath) return false // procedural = instant
    return !this.modelCache.has(aircraft.aircraftType.typeCode)
  }

  /**
   * Load a model from network, cache the template, and resolve with it.
   * Only called once per typeCode even if multiple aircraft request concurrently.
   */
  private loadFromNetwork(filePath: string, typeCode: string, tailNumber: string): Promise<THREE.Object3D> {
    return new Promise((resolve, reject) => {
      this.loader.load(
        filePath,
        (gltf) => {
          const template = gltf.scene

          // Fix face culling: scene's negative Y scale (makeScale(s, -s, s)) reverses
          // winding order, but it's applied via camera projection, not world matrices,
          // so Three.js can't auto-detect the flip. DoubleSide renders both faces.
          template.traverse((child) => {
            if (child instanceof THREE.Mesh) {
              if (Array.isArray(child.material)) {
                child.material.forEach((mat) => { mat.side = THREE.DoubleSide })
              } else {
                child.material.side = THREE.DoubleSide
              }
            }
          })

          // Cache a clean copy as the template for future clones
          this.modelCache.set(typeCode, template.clone())
          this.pendingLoads.delete(typeCode)
          console.log(`AircraftLoader: Loaded ${tailNumber} (${typeCode})`)
          resolve(template)
        },
        (progress) => {
          if (progress.total > 0) {
            const percent = (progress.loaded / progress.total * 100).toFixed(0)
            console.log(`Loading ${typeCode}: ${percent}%`)
          }
        },
        (error) => {
          console.error(`Error loading aircraft model ${filePath}:`, error)
          this.pendingLoads.delete(typeCode)
          reject(error)
        }
      )
    })
  }

  /**
   * Deep-clone geometry and materials on every Mesh so the instance
   * owns its own GPU resources.  Disposal of the instance will NOT
   * destroy the cached template's buffers/programs.
   */
  private cloneGpuResources(root: THREE.Object3D): void {
    root.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry = child.geometry.clone()
        if (Array.isArray(child.material)) {
          child.material = child.material.map((m: THREE.Material) => m.clone())
        } else {
          child.material = child.material.clone()
        }
      }
    })
  }

  /**
   * Clone a template model and apply per-instance transform + shadow config.
   * Single clone — no double-cloning. GPU resources are deep-cloned so
   * disposal is safe (won't destroy shared template buffers).
   */
  private createInstanceFromTemplate(template: THREE.Object3D, aircraft: PlacedAircraft3D): THREE.Object3D {
    const instance = template.clone()
    instance.name = aircraft.id
    this.cloneGpuResources(instance)
    this.applyTransform(instance, aircraft)

    instance.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.castShadow = this.enableShadows
        child.receiveShadow = this.enableShadows
      }
    })

    return instance
  }

  /**
   * Apply position, rotation, and scale transform to a model
   */
  private applyTransform(model: THREE.Object3D, aircraft: PlacedAircraft3D): void {
    const { aircraftType, position, rotation, scale } = aircraft
    const settings = aircraftType.modelSettings

    // Get values from settings with defaults
    const modelScale = settings.scale ?? 1
    const modelRotation = settings.rotation ?? [0, 0, 0]
    const yOffset = settings.yOffset ?? 0
    const headingAxis = settings.headingAxis ?? 'y'

    // Convert position from feet to meters for Mapbox, apply y-offset
    const posX = position[0] * FEET_TO_METERS
    const posY = (position[1] + yOffset) * FEET_TO_METERS
    const posZ = position[2] * FEET_TO_METERS
    model.position.set(posX, posY, posZ)

    // Apply base rotation from model settings (to orient the model correctly)
    const baseRotX = (modelRotation[0] * Math.PI) / 180
    const baseRotY = (modelRotation[1] * Math.PI) / 180
    const baseRotZ = (modelRotation[2] * Math.PI) / 180

    // Apply heading rotation from allocation on top of base rotation
    const headingRad = (rotation * Math.PI) / 180

    // Combine base rotation with heading based on heading axis
    if (headingAxis === 'y') {
      model.rotation.set(baseRotX, baseRotY + headingRad, baseRotZ)
    } else if (headingAxis === 'z') {
      model.rotation.set(baseRotX, baseRotY, baseRotZ + headingRad)
    } else {
      model.rotation.set(baseRotX + headingRad, baseRotY, baseRotZ)
    }

    // Apply scale (model scale from type × instance scale)
    const finalScale = modelScale * scale
    model.scale.set(finalScale, finalScale, finalScale)

    // Stash the per-model transform base so updateModelTransform() can re-derive
    // a consistent pose later (animation) without re-reading modelSettings.
    model.userData.transformBase = {
      kind: 'gltf',
      yOffsetFeet: yOffset,
      baseRot: [baseRotX, baseRotY, baseRotZ] as [number, number, number],
      headingAxis,
      proceduralGroundY: 0,
    }
  }

  /**
   * Create a placeholder box model when GLTF fails to load
   */
  private createPlaceholderModel(aircraft: PlacedAircraft3D): THREE.Object3D {
    // Create a simple box as placeholder (sized like a small aircraft)
    const geometry = new THREE.BoxGeometry(
      15 * FEET_TO_METERS,  // width ~15 feet
      5 * FEET_TO_METERS,   // height ~5 feet
      40 * FEET_TO_METERS   // length ~40 feet
    )
    const material = new THREE.MeshStandardMaterial({
      color: 0x888888,
      transparent: true,
      opacity: 0.7,
    })
    const mesh = new THREE.Mesh(geometry, material)
    mesh.name = aircraft.id

    // Apply transform
    const posX = aircraft.position[0] * FEET_TO_METERS
    const posY = aircraft.position[1] * FEET_TO_METERS + (2.5 * FEET_TO_METERS) // Lift to center
    const posZ = aircraft.position[2] * FEET_TO_METERS
    mesh.position.set(posX, posY, posZ)

    const rotationRad = (aircraft.rotation * Math.PI) / 180
    mesh.rotation.set(0, rotationRad, 0)

    if (this.enableShadows) {
      mesh.castShadow = true
      mesh.receiveShadow = true
    }

    console.log(`AircraftLoader: Created placeholder for ${aircraft.tailNumber}`)
    return mesh
  }

  /**
   * Create a procedural aircraft model from physical dimensions.
   * Uses category-specific shapes (fuselage, wings, tail, engines).
   * Cached by typeCode with 'procedural:' prefix to avoid GLTF cache collisions.
   */
  private createProceduralModel(aircraft: PlacedAircraft3D): THREE.Object3D {
    const dims = aircraft.physicalDimensions!
    const cacheKey = `procedural:${aircraft.aircraftType.typeCode}`

    // Check procedural cache first
    const cached = this.modelCache.get(cacheKey)
    if (cached) {
      const instance = cached.clone()
      instance.name = aircraft.id
      this.cloneGpuResources(instance)
      // Procedural models use neutral transform settings
      this.applyProceduralTransform(instance, aircraft)
      this.configureShadows(instance)
      return instance
    }

    // Generate procedural model
    const proceduralGroup = ProceduralAircraftFactory.create({
      length: dims.length,
      wingspan: dims.wingspan,
      height: dims.height,
      tailHeight: dims.tailHeight,
      category: dims.category,
      gearType: dims.gearType,
    })

    // Cache the template for reuse
    this.modelCache.set(cacheKey, proceduralGroup.clone())

    proceduralGroup.name = aircraft.id
    this.applyProceduralTransform(proceduralGroup, aircraft)
    this.configureShadows(proceduralGroup)

    console.log(
      `AircraftLoader: Created procedural model for ${aircraft.tailNumber} (${dims.category || 'unknown'})`
    )
    return proceduralGroup
  }

  /**
   * Apply transform for procedural models.
   * Procedural models are already in meters with nose at +Z,
   * so we use neutral model settings (no base rotation, scale=1, no yOffset).
   */
  private applyProceduralTransform(model: THREE.Object3D, aircraft: PlacedAircraft3D): void {
    // Capture the factory-baked ground clearance BEFORE overwriting position.
    const proceduralGroundY = model.position.y

    // Convert position from feet to meters
    const posX = aircraft.position[0] * FEET_TO_METERS
    const posY = aircraft.position[1] * FEET_TO_METERS
    const posZ = aircraft.position[2] * FEET_TO_METERS
    // Preserve the y-offset set by the factory (ground clearance)
    model.position.set(posX, posY + proceduralGroundY, posZ)

    // Apply heading rotation on Y axis
    const headingRad = (aircraft.rotation * Math.PI) / 180
    model.rotation.set(0, headingRad, 0)

    // Apply instance scale
    const s = aircraft.scale
    model.scale.set(s, s, s)

    // Stash the transform base. Procedural models bake their own ground
    // clearance into model.position.y, so preserve it on re-apply.
    model.userData.transformBase = {
      kind: 'procedural',
      yOffsetFeet: 0,
      baseRot: [0, 0, 0] as [number, number, number],
      headingAxis: 'y' as const,
      proceduralGroundY,
    }
  }

  /**
   * Re-apply position (feet) + heading (degrees) to an already-loaded model,
   * reusing the transform base captured at load time. Works for both GLTF and
   * procedural models — taxiing aircraft stay visually identical to parked ones.
   * `positionFeet[1]` carries altitude (feet) for airborne legs.
   * Does NOT mark the scene dirty — the caller batches one repaint per frame.
   */
  updateModelTransform(
    model: THREE.Object3D,
    positionFeet: [number, number, number],
    rotationDeg: number
  ): void {
    const base = model.userData.transformBase as
      | {
          kind: 'gltf' | 'procedural'
          yOffsetFeet: number
          baseRot: [number, number, number]
          headingAxis: 'x' | 'y' | 'z'
          proceduralGroundY: number
        }
      | undefined

    // Position: feet → meters, plus the captured ground clearance / y-offset.
    const posX = positionFeet[0] * FEET_TO_METERS
    const yOffsetFeet = base?.yOffsetFeet ?? 0
    const posY =
      (positionFeet[1] + yOffsetFeet) * FEET_TO_METERS + (base?.proceduralGroundY ?? 0)
    const posZ = positionFeet[2] * FEET_TO_METERS
    model.position.set(posX, posY, posZ)

    // Heading on top of the model's base rotation, on its heading axis.
    const headingRad = (rotationDeg * Math.PI) / 180
    const [bx, by, bz] = base?.baseRot ?? [0, 0, 0]
    const axis = base?.headingAxis ?? 'y'
    if (axis === 'y') {
      model.rotation.set(bx, by + headingRad, bz)
    } else if (axis === 'z') {
      model.rotation.set(bx, by, bz + headingRad)
    } else {
      model.rotation.set(bx + headingRad, by, bz)
    }
  }

  /**
   * Apply ghost materials to a model based on render mode.
   * Swaps solid materials for transparent ghost variants on the cloned instance.
   * Only applies to non-solid render modes.
   */
  private applyGhostMaterials(model: THREE.Object3D, renderMode: AllocationRenderMode): void {
    if (renderMode === 'solid') return

    // Select appropriate ghost material set
    let ghostBody: THREE.MeshStandardMaterial
    let ghostAccent: THREE.MeshStandardMaterial

    switch (renderMode) {
      case 'ghost_away':
        ghostBody = getGhostAwayBodyMaterial()
        ghostAccent = getGhostAwayAccentMaterial()
        break
      case 'ghost_incoming':
        ghostBody = getGhostIncomingBodyMaterial()
        ghostAccent = getGhostIncomingAccentMaterial()
        break
      case 'ghost_returning':
        ghostBody = getGhostReturningBodyMaterial()
        ghostAccent = getGhostReturningAccentMaterial()
        break
      default:
        return
    }

    const solidBody = getBodyMaterial()
    const solidAccent = getAccentMaterial()

    model.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        // Swap materials from solid to ghost
        if (child.material === solidBody) {
          child.material = ghostBody
        } else if (child.material === solidAccent) {
          child.material = ghostAccent
        } else if (child.material instanceof THREE.MeshStandardMaterial && !child.material.transparent) {
          // For GLTF models or unknown materials, make them transparent
          child.material = child.material.clone()
          child.material.transparent = true
          child.material.opacity = 0.2
          child.material.depthWrite = false
          child.material.side = THREE.DoubleSide
        }
        // Ghost models don't cast shadows (they're translucent indicators)
        child.castShadow = false
      }
    })

    // Store renderMode on the model for label overlay to detect
    model.userData.renderMode = renderMode
  }

  /**
   * Configure shadow casting/receiving on all meshes in a model
   */
  private configureShadows(model: THREE.Object3D): void {
    model.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.castShadow = this.enableShadows
        child.receiveShadow = this.enableShadows
      }
    })
  }

  /**
   * Create a simplified box model for LOD-low (far zoom).
   * Much cheaper than full GLTF — 1 mesh vs ~20.
   * Preserves same name/position/rotation for seamless swap.
   */
  createSimplifiedModel(aircraft: PlacedAircraft3D): THREE.Object3D {
    // Reuse the shared geometry/material — no per-aircraft GPU allocation.
    const mesh = new THREE.Mesh(getSharedBoxGeometry(), getSharedBoxMaterial())
    mesh.name = aircraft.id
    // Flag shared resources so disposeObject() skips disposing them.
    mesh.userData.shared = true

    this.applyTransform(mesh, aircraft)
    // Lift slightly so box sits on ground, not sunk halfway
    mesh.position.y += 1.5 * FEET_TO_METERS

    if (this.enableShadows) {
      mesh.castShadow = true
      mesh.receiveShadow = true
    }
    return mesh
  }

  /**
   * Update aircraft transform (for repositioning)
   */
  updateAircraftTransform(model: THREE.Object3D, aircraft: PlacedAircraft3D): void {
    this.applyTransform(model, aircraft)
  }

  /**
   * Clear model cache and dispose GPU resources
   */
  clearCache(): void {
    for (const [, model] of this.modelCache) {
      model.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.geometry?.dispose()
          const mats = Array.isArray(child.material) ? child.material : [child.material]
          mats.forEach((m) => {
            if (m.map) m.map.dispose()
            if (m.normalMap) m.normalMap.dispose()
            if (m.roughnessMap) m.roughnessMap.dispose()
            m.dispose()
          })
        }
      })
    }
    this.modelCache.clear()
  }

  /**
   * Load multiple aircraft models grouped by type.
   *
   * Groups aircraft by typeCode so each model file is fetched once,
   * then all aircraft of that type are cloned from cache.
   * Processes one type group at a time: 1 network fetch → N instant clones.
   *
   * @param opts.onTypeGroupLoaded  Called after each type group finishes loading.
   *   Receives the loaded models and the typeCode so the caller can stream them
   *   into the scene incrementally (e.g., fade out sprites for that type).
   */
  async loadAircraftBatch(
    aircraft: PlacedAircraft3D[],
    forceProceduralMode: boolean = false,
    opts?: {
      onTypeGroupLoaded?: (models: THREE.Object3D[], typeCode: string) => void
    }
  ): Promise<THREE.Object3D[]> {
    const { onTypeGroupLoaded } = opts ?? {}

    // Group by typeCode — all aircraft sharing the same model load together
    const byType = new Map<string, PlacedAircraft3D[]>()
    for (const ac of aircraft) {
      const key = ac.aircraftType.typeCode
      if (!byType.has(key)) byType.set(key, [])
      byType.get(key)!.push(ac)
    }

    // Sort type groups by count descending (most common types first)
    const sortedGroups = [...byType.entries()].sort((a, b) => b[1].length - a[1].length)

    const allModels: THREE.Object3D[] = []

    // Process one type at a time: 1 network fetch → N clones
    for (const [typeCode, group] of sortedGroups) {
      const groupModels = await Promise.all(
        group.map((ac) => this.loadAircraft(ac, forceProceduralMode))
      )
      allModels.push(...groupModels)
      onTypeGroupLoaded?.(groupModels, typeCode)
    }

    return allModels
  }

  /**
   * Preload a model into cache for faster loading later.
   * Call this when user selects an aircraft type to have the model
   * ready before they enter placement mode.
   *
   * @param typeCode Aircraft type code (cache key)
   * @param modelUrl URL to GLTF model
   */
  async preloadModel(typeCode: string, modelUrl: string): Promise<void> {
    // Already cached? Skip
    if (this.modelCache.has(typeCode)) {
      console.log(`AircraftLoader: ${typeCode} already cached, skipping preload`)
      return
    }

    // Already loading? Wait for it
    if (this.pendingLoads.has(typeCode)) {
      await this.pendingLoads.get(typeCode)
      return
    }

    return new Promise((resolve) => {
      this.loader.load(
        modelUrl,
        (gltf) => {
          // Cache the model
          this.modelCache.set(typeCode, gltf.scene.clone())
          console.log(`AircraftLoader: Preloaded ${typeCode}`)
          resolve()
        },
        undefined,
        (error) => {
          console.warn(`AircraftLoader: Failed to preload ${typeCode}:`, error)
          resolve() // Don't reject - preload failure shouldn't block UI
        }
      )
    })
  }
}

// Singleton instance for sharing between components
let sharedInstance: AircraftLoaderService | null = null

/**
 * Get or create the shared AircraftLoaderService instance.
 * Use this to ensure preloaded models are available across components.
 */
export function getAircraftLoaderInstance(): AircraftLoaderService {
  if (!sharedInstance) {
    sharedInstance = new AircraftLoaderService(true)
  }
  return sharedInstance
}
